import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../styles/SelettorePrestazione.css';

/**
 * SelettorePrestazione — sostituisce la tendina nativa, che con
 * centinaia di voci e' inutilizzabile: si scrive e si filtra.
 *
 * Props:
 *  - voci        elenco delle prestazioni disponibili
 *  - valore      id selezionato
 *  - onChange    (id) => void
 *  - placeholder
 */
const SelettorePrestazione = ({ voci, valore, onChange, placeholder }) => {
    const [aperto, setAperto] = useState(false);
    const [ricerca, setRicerca] = useState('');
    const [evidenziato, setEvidenziato] = useState(0);
    const contenitore = useRef(null);
    const campo = useRef(null);

    const scelta = useMemo(
        () => voci.find((v) => String(v.id) === String(valore)),
        [voci, valore]
    );

    const filtrate = useMemo(() => {
        const q = ricerca.trim().toLowerCase();
        if (!q) return voci.slice(0, 60);
        return voci
            .filter((v) =>
                (v.nome || '').toLowerCase().includes(q) ||
                (v.codice || '').toLowerCase().includes(q))
            .slice(0, 60);
    }, [voci, ricerca]);

    /* Chiusura al clic fuori */
    useEffect(() => {
        const fuori = (e) => {
            if (contenitore.current && !contenitore.current.contains(e.target)) {
                setAperto(false);
                setRicerca('');
            }
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, []);

    useEffect(() => {
        if (aperto && campo.current) campo.current.focus();
    }, [aperto]);

    const scegli = (v) => {
        onChange(v ? v.id : '');
        setAperto(false);
        setRicerca('');
    };

    const tasti = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setEvidenziato((i) => Math.min(i + 1, filtrate.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEvidenziato((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtrate[evidenziato]) scegli(filtrate[evidenziato]);
        } else if (e.key === 'Escape') {
            setAperto(false);
            setRicerca('');
        }
    };

    return (
        <div className="sprest" ref={contenitore}>
            {aperto ? (
                <input
                    ref={campo}
                    className="sprest-input"
                    value={ricerca}
                    placeholder="Scrivi per cercare…"
                    onChange={(e) => { setRicerca(e.target.value); setEvidenziato(0); }}
                    onKeyDown={tasti}
                />
            ) : (
                <button
                    type="button"
                    className={`sprest-chiuso ${scelta ? '' : 'vuoto'}`}
                    onClick={() => setAperto(true)}
                >
                    {scelta ? scelta.nome : (placeholder || '-- Seleziona prestazione --')}
                    <span className="sprest-caret">▾</span>
                </button>
            )}

            {aperto && (
                <div className="sprest-lista">
                    {filtrate.length === 0 && (
                        <div className="sprest-vuoto">Nessuna prestazione trovata</div>
                    )}
                    {filtrate.map((v, i) => (
                        <button
                            key={v.id}
                            type="button"
                            className={`sprest-voce ${i === evidenziato ? 'evidenziata' : ''}`}
                            onMouseEnter={() => setEvidenziato(i)}
                            onClick={() => scegli(v)}
                        >
                            <span className="sprest-nome">{v.nome}</span>
                            <span className="sprest-lato">
                                {v.ammette_esenzione && (
                                    <span className="sprest-esente" title="Può essere esentata con attestazione">
                                        esentabile
                                    </span>
                                )}
                                {v.prezzo_indicativo != null && (
                                    <span className="sprest-prezzo">
                                        {(parseFloat(v.prezzo_indicativo) || 0)
                                            .toLocaleString('it-IT', { minimumFractionDigits: 2 })} €
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                    {voci.length > filtrate.length && !ricerca && (
                        <div className="sprest-vuoto">
                            Altre {voci.length - filtrate.length} voci: scrivi per cercarle
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelettorePrestazione;
